import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, take, takeUntil } from 'rxjs';
import { HasSubscription } from 'src/ca-shared/has-subscription/has-subscription';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { DateHelper } from 'src/core/helpers/date.helper';
import { DepartmentDto } from 'src/core/models/administration/department/department.dto';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { TargetSelectionType } from 'src/core/models/enum/target-selection-type.enum';
import { DepartmentFilterModeOption } from 'src/core/models/generic-lookup-type/department/department-filter-mode-option.glt';
import { InsightType } from 'src/core/models/generic-lookup-type/insight/insight-type.glt';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { LLMConfigurationDto } from 'src/core/models/llm/llm-configuration.dto';
import { CategoryDto } from 'src/core/models/query/category.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { UserGroupDto } from 'src/core/models/user-group/user-group.dto';
import { UserCacheService } from 'src/core/services/cache/user-cache.service';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { IntegerValidator } from 'src/core/validators/shared/integer.validator';

@Component({
  selector: 'ca-ai-insight-settings',
  templateUrl: './ai-insight-settings.component.html',
  styleUrls: ['./ai-insight-settings.component.scss'],
  standalone: false,
})
@AutoUnsubscribe()
export class AiInsightSettingsComponent extends HasSubscription implements OnInit {
  aiInsightSettingsForm: FormGroup;
  aiInsightSettingDtos: ConfigurationSettingsDto[] = [];
  aiInsightsEnabled: boolean;
  providerTypes: GenericLookupDto[];
  llmConfigurations: LLMConfigurationDto[] = [];
  insightTypes: GenericLookupDto[];
  insightTypesData: string[] = [];
  languagesDataSource: CallLanguageCodeDto[];
  defaultLanguageCode: string;
  languageCode: string;
  aiInsightsDailyLimit: number = Number.MAX_SAFE_INTEGER;
  whiteLabelingEnabled: boolean = false;

  private readonly settingKeyAiInsightsEnabled = 'GenAI.AIInsightsEnabled';
  private readonly settingKeyInsightTypes = 'Insight.InsightType';
  private readonly settingKeyLanguageCode = 'Insight.LanguageCode';
  private readonly settingKeyLLMConfigurationId = 'Insight.LLMConfigurationId';
  private readonly settingKeyCustomizeSummarizationEnabled =
    'Insight.CustomizeSummarizationEnabled';
  private readonly settingKeyPromptPrefix = 'Insight.PromptPrefix';
  private readonly automaticSummarizationPrefix = 'AutomaticSummarization';
  private readonly automaticSummarizationEnabled = `${this.automaticSummarizationPrefix}.Enabled`;
  private readonly automaticSummarizationFilterConversations = `${this.automaticSummarizationPrefix}.FilterConversations`;
  private readonly automaticSummarizationCategories = `${this.automaticSummarizationPrefix}.Categories`;
  private readonly automaticSummarizationDailyConversationLimit = `${this.automaticSummarizationPrefix}.DailyConversationLimit`;

  private readonly aiCoachingPrefix = 'AICoaching';
  private readonly aiCoachingEnabled = `${this.aiCoachingPrefix}.Enabled`;
  private readonly aiCoachingStartDate = `${this.aiCoachingPrefix}.StartDate`;
  private readonly aiCoachingPeriod = `${this.aiCoachingPrefix}.Period`;
  private readonly aiCoachingTargetSelection = `${this.aiCoachingPrefix}.TargetSelection`;
  private readonly aiCoachingTargets = `${this.aiCoachingPrefix}.Targets`;
  private readonly aiCoachingMaxConversationCountPerAgent = `${this.aiCoachingPrefix}.MaxConversationCountPerAgent`;
  private readonly aiCoachingGoalKpiAvgCallDuration = `${this.aiCoachingPrefix}.GoalKpi.AvgCallDuration`;
  private readonly aiCoachingGoalKpiAvgSilenceRatio = `${this.aiCoachingPrefix}.GoalKpi.AvgSilenceRatio`;
  private readonly aiCoachingGoalKpiAvgCustomerShare = `${this.aiCoachingPrefix}.GoalKpi.AvgCustomerShare`;
  private readonly aiCoachingGoalKpiAvgHoldCount = `${this.aiCoachingPrefix}.GoalKpi.AvgHoldCount`;
  private readonly aiCoachingGoalKpiAvgAgentMonologueRatio = `${this.aiCoachingPrefix}.GoalKpi.AvgAgentMonologueRatio`;
  private readonly aiCoachingGoalKpiAvgAgentSpeed = `${this.aiCoachingPrefix}.GoalKpi.AvgAgentSpeed`;
  private readonly aiCoachingGoalKpiAvgAgentInterruptions = `${this.aiCoachingPrefix}.GoalKpi.AvgAgentInterruptions`;

  private readonly aiCategoryDiscoveryPrefix = 'AICategoryDiscovery';
  private readonly aiCategoryDiscoveryEnabled = `${this.aiCategoryDiscoveryPrefix}.Enabled`;
  private readonly aiCategoryDiscoveryLlmConfigurationId = `${this.aiCategoryDiscoveryPrefix}.LLMConfigurationId`;

  private readonly defaultValues = ['Insight.Type.Topic', 'Insight.Type.Summary'];
  private readonly aiCoachingDefaultValues = [
    'Insight.Type.PositiveFeedbacks',
    'Insight.Type.NegativeFeedbacks',
    'Insight.Type.ImprovementAreas',
  ];

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private store: Store,
    private userCacheService: UserCacheService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private crudService: CrudService,
    private operators: Operators,
    private dateHelper: DateHelper,
    private targetSelectionType: TargetSelectionType,
  ) {
    super();

    this.whiteLabelingEnabled =
      this.config.getSetting('WhiteLabeling:Enabled')?.toLowerCase() === 'true';
    this.aiInsightsEnabled =
      this.config.getSetting(this.settingKeyAiInsightsEnabled)?.toLowerCase() !== 'false';

    let aiCoachingStartDateObj = this.dateHelper.getDate(
      this.config.getSetting(this.aiCoachingStartDate),
    );
    if (!aiCoachingStartDateObj) {
      let now = new Date();
      aiCoachingStartDateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else {
      aiCoachingStartDateObj = new Date(
        aiCoachingStartDateObj.getUTCFullYear(),
        aiCoachingStartDateObj.getUTCMonth(),
        aiCoachingStartDateObj.getUTCDate(),
      );
    }

    this.crudService
      .get<LLMConfigurationDto>(LLMConfigurationDto, {
        filters: [],
        sorters: [],
        skipCount: 0,
        maxResultCount: 9999,
      })
      .subscribe(response => {
        this.llmConfigurations = response.items;
      });

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(
        map(filterFn => filterFn(InsightType)),
        map(result =>
          result.map(item => ({
            ...item,
            disabled:
              this.defaultValues.includes(item.code) ||
              this.aiCoachingDefaultValues.includes(item.code),
          })),
        ),
      )
      .subscribe(modifiedResult => {
        this.insightTypes = modifiedResult;
      });

    let request = {
      filters: [
        {
          field: 'isVisible',
          operator: this.operators.Contains,
          value: true,
        },
        {
          field: 'withoutMixed',
          operator: this.operators.Contains,
          value: true,
        },
      ],
      sorters: [],
      maxResultCount: 99999,
      skipCount: 0,
    };

    this.settingService
      .getLanguageCodeList(request)
      .pipe(take(1))
      .subscribe(res => {
        this.languagesDataSource = res.items;
      });

    let automaticSummarizationFilterConversations = this.config
      .getSetting(this.automaticSummarizationFilterConversations)
      ?.toLowerCase();

    this.insightTypesData = this.getInsightTypes(
      this.config.getSetting(this.settingKeyInsightTypes),
      this.config.getSetting(this.aiCoachingEnabled).toLowerCase() === 'true',
    );

    automaticSummarizationFilterConversations =
      automaticSummarizationFilterConversations === null
        ? 'false'
        : automaticSummarizationFilterConversations;

    this.defaultLanguageCode = this.config.getSetting('Global.SystemLanguageCode');
    let languageCode = this.config.getSetting(this.settingKeyLanguageCode);
    if (!languageCode) {
      this.languageCode = this.defaultLanguageCode;
    } else {
      this.languageCode = languageCode;
    }
    let llmConfigurationId;
    if (this.config.getSetting(this.settingKeyLLMConfigurationId)) {
      llmConfigurationId = Number(this.config.getSetting(this.settingKeyLLMConfigurationId));
    }

    let aiCategoryDiscoveryLlmConfigId;
    if (this.config.getSetting(this.aiCategoryDiscoveryLlmConfigurationId)) {
      aiCategoryDiscoveryLlmConfigId = Number(
        this.config.getSetting(this.aiCategoryDiscoveryLlmConfigurationId),
      );
    }

    this.aiInsightSettingsForm = fb.group({
      aiInsightsEnabled: [this.aiInsightsEnabled],
      insightTypes: [this.insightTypesData],
      languageCode: this.languageCode,
      llmConfigurationId: [llmConfigurationId],
      promptPrefix: [this.config.getSetting(this.settingKeyPromptPrefix)],
      customizeSummarizationEnabled: [
        JSON.parse(
          this.config.getSetting(this.settingKeyCustomizeSummarizationEnabled).toLowerCase(),
        ),
      ],
      automaticSummarizationEnabled: [
        JSON.parse(this.config.getSetting(this.automaticSummarizationEnabled).toLowerCase()),
      ],
      automaticSummarizationFilterConversations: [automaticSummarizationFilterConversations],
      automaticSummarizationCategories: [null],
      automaticSummarizationDailyConversationLimit: [
        this.config.getSetting(this.automaticSummarizationDailyConversationLimit),
      ],
      aiCategoryDiscoveryEnabled: [
        this.config.getSetting(this.aiCategoryDiscoveryEnabled)?.toLowerCase() === 'true',
      ],
      aiCategoryDiscoveryLlmConfigurationId: [aiCategoryDiscoveryLlmConfigId],
      aiCoachingEnabled: [JSON.parse(this.config.getSetting(this.aiCoachingEnabled).toLowerCase())],
      aiCoachingStartDate: [aiCoachingStartDateObj],
      aiCoachingPeriod: [this.config.getSetting(this.aiCoachingPeriod)],
      aiCoachingTargetSelection: [this.config.getSetting(this.aiCoachingTargetSelection)],
      aiCoachingTargets: [this.config.getSetting(this.aiCoachingTargets)],
      aiCoachingMaxConversationCountPerAgent: [
        this.config.getSetting(this.aiCoachingMaxConversationCountPerAgent),
        { validators: [IntegerValidator.minMax(1, 500)] },
      ],
      aiCoachingAgents: [null],
      aiCoachingDepartments: [null],
      aiCoachingUserGroups: [null],
      aiCoachingGoalKpiAvgCallDuration: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgCallDuration) || null,
      ],
      aiCoachingGoalKpiAvgSilenceRatio: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgSilenceRatio) || null,
      ],
      aiCoachingGoalKpiAvgCustomerShare: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgCustomerShare) || null,
      ],
      aiCoachingGoalKpiAvgHoldCount: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgHoldCount) || null,
      ],
      aiCoachingGoalKpiAvgAgentMonologueRatio: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgAgentMonologueRatio) || null,
      ],
      aiCoachingGoalKpiAvgAgentSpeed: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgAgentSpeed) || null,
      ],
      aiCoachingGoalKpiAvgAgentInterruptions: [
        this.config.getSetting(this.aiCoachingGoalKpiAvgAgentInterruptions) || null,
      ],
    });

    this.setTargets();

    if (this.insightTypesData.length > 0) {
      this.aiInsightSettingsForm.get('insightTypes').setValue(this.insightTypesData);
    }

    this.aiInsightSettingsForm
      .get('aiCoachingEnabled')
      .valueChanges.pipe(takeUntil(this.autoUnsubscribeNotifier))
      .subscribe(value => {
        this.insightTypesData = this.getInsightTypes(
          this.aiInsightSettingsForm.get('insightTypes').value?.join(';'),
          value,
        );

        this.aiInsightSettingsForm.get('insightTypes').patchValue(this.insightTypesData);
      });

    this.loadCategories();
  }

  ngOnInit(): void {
    this.settingService
      .getAIInsightsDailyLimit()
      .pipe(take(1))
      .subscribe(limit => {
        this.aiInsightsDailyLimit = limit;
      });
  }

  onSubmitAiInsightSettings() {
    if (this.aiInsightSettingsForm.invalid) {
      return;
    }

    this.aiInsightSettingDtos = new Array();

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyAiInsightsEnabled,
      settingValue: this.aiInsightSettingsForm.get('aiInsightsEnabled').value.toString(),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyInsightTypes,
      settingValue: this.aiInsightSettingsForm.get('insightTypes').value?.join(';'),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyLanguageCode,
      settingValue: this.aiInsightSettingsForm.get('languageCode').value.toString(),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyLLMConfigurationId,
      settingValue: this.aiInsightSettingsForm.get('llmConfigurationId').value?.toString(),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyCustomizeSummarizationEnabled,
      settingValue: this.aiInsightSettingsForm
        .get('customizeSummarizationEnabled')
        .value.toString(),
    });

    if (this.aiInsightSettingsForm.get('customizeSummarizationEnabled').value) {
      this.aiInsightSettingDtos.push({
        settingName: this.settingKeyPromptPrefix,
        settingValue: this.aiInsightSettingsForm.get('promptPrefix').value.toString(),
      });
    }

    this.aiInsightSettingDtos.push({
      settingName: this.automaticSummarizationEnabled,
      settingValue: this.aiInsightSettingsForm
        .get('automaticSummarizationEnabled')
        .value.toString(),
    });

    if (this.aiInsightSettingsForm.get('automaticSummarizationEnabled').value) {
      this.aiInsightSettingDtos.push({
        settingName: this.automaticSummarizationFilterConversations,
        settingValue: this.aiInsightSettingsForm.get('automaticSummarizationFilterConversations')
          .value,
      });

      this.aiInsightSettingDtos.push({
        settingName: this.automaticSummarizationCategories,
        settingValue: JSON.stringify(
          this.aiInsightSettingsForm.get('automaticSummarizationCategories').value?.map(x => x.id),
        ),
      });

      this.aiInsightSettingDtos.push({
        settingName: this.automaticSummarizationDailyConversationLimit,
        settingValue: this.aiInsightSettingsForm
          .get('automaticSummarizationDailyConversationLimit')
          .value.toString(),
      });

      this.aiInsightSettingDtos.push({
        settingName: this.settingKeyPromptPrefix,
        settingValue: this.aiInsightSettingsForm.get('promptPrefix').value.toString(),
      });

      this.aiInsightSettingDtos.push({
        settingName: this.aiCategoryDiscoveryEnabled,
        settingValue: this.aiInsightSettingsForm.get('aiCategoryDiscoveryEnabled').value.toString(),
      });

      if (this.aiInsightSettingsForm.get('aiCategoryDiscoveryEnabled').value) {
        this.aiInsightSettingDtos.push({
          settingName: this.aiCategoryDiscoveryLlmConfigurationId,
          settingValue: this.aiInsightSettingsForm
            .get('aiCategoryDiscoveryLlmConfigurationId')
            .value?.toString(),
        });
      }

      this.aiInsightSettingDtos.push({
        settingName: this.aiCoachingEnabled,
        settingValue: this.aiInsightSettingsForm.get('aiCoachingEnabled').value.toString(),
      });

      if (this.aiInsightSettingsForm.get('aiCoachingEnabled').value) {
        let aiCoachingStartDate: Date = this.aiInsightSettingsForm.get('aiCoachingStartDate').value;
        aiCoachingStartDate = new Date(
          Date.UTC(
            aiCoachingStartDate.getFullYear(),
            aiCoachingStartDate.getMonth(),
            aiCoachingStartDate.getDate(),
          ),
        );
        this.aiInsightSettingDtos.push({
          settingName: this.aiCoachingStartDate,
          settingValue: this.dateHelper.convertDateToTicks(aiCoachingStartDate).toString(),
        });

        this.aiInsightSettingDtos.push({
          settingName: this.aiCoachingPeriod,
          settingValue: this.aiInsightSettingsForm.get('aiCoachingPeriod').value.toString(),
        });

        this.aiInsightSettingDtos.push({
          settingName: this.aiCoachingTargetSelection,
          settingValue: this.aiInsightSettingsForm
            .get('aiCoachingTargetSelection')
            .value.toString(),
        });

        this.aiInsightSettingDtos.push({
          settingName: this.aiCoachingTargets,
          settingValue: this.getTargets(),
        });

        this.aiInsightSettingDtos.push({
          settingName: this.aiCoachingMaxConversationCountPerAgent,
          settingValue: this.aiInsightSettingsForm
            .get('aiCoachingMaxConversationCountPerAgent')
            .value.toString(),
        });

        const kpiFields: { key: string; setting: string }[] = [
          {
            key: 'aiCoachingGoalKpiAvgCallDuration',
            setting: this.aiCoachingGoalKpiAvgCallDuration,
          },
          {
            key: 'aiCoachingGoalKpiAvgSilenceRatio',
            setting: this.aiCoachingGoalKpiAvgSilenceRatio,
          },
          {
            key: 'aiCoachingGoalKpiAvgCustomerShare',
            setting: this.aiCoachingGoalKpiAvgCustomerShare,
          },
          { key: 'aiCoachingGoalKpiAvgHoldCount', setting: this.aiCoachingGoalKpiAvgHoldCount },
          {
            key: 'aiCoachingGoalKpiAvgAgentMonologueRatio',
            setting: this.aiCoachingGoalKpiAvgAgentMonologueRatio,
          },
          { key: 'aiCoachingGoalKpiAvgAgentSpeed', setting: this.aiCoachingGoalKpiAvgAgentSpeed },
          {
            key: 'aiCoachingGoalKpiAvgAgentInterruptions',
            setting: this.aiCoachingGoalKpiAvgAgentInterruptions,
          },
        ];
        kpiFields.forEach(({ key, setting }) => {
          const raw = this.aiInsightSettingsForm.get(key).value;
          const normalized = raw != null && raw !== '' ? raw.toString().replace(',', '.') : '';
          const parsed = normalized !== '' ? parseFloat(normalized) : NaN;
          this.aiInsightSettingDtos.push({
            settingName: setting,
            settingValue: !isNaN(parsed) ? parsed.toString() : '',
          });
        });
      }
    } else {
      this.aiInsightSettingDtos.push({
        settingName: this.aiCoachingEnabled,
        settingValue: 'false',
      });

      this.aiInsightSettingDtos.push({
        settingName: this.aiCategoryDiscoveryEnabled,
        settingValue: 'false',
      });
    }

    this.settingService.saveSetting(this.aiInsightSettingDtos).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
      );
    });
  }

  isDisabled(code: string) {
    if (this.defaultValues.includes(code) || this.aiCoachingDefaultValues.includes(code)) {
      return true;
    }

    return false;
  }

  removeFromSelectedInsights(insightCode: string) {
    let selectedItems = this.aiInsightSettingsForm.get('insightTypes').value;

    if (selectedItems) {
      const indexToRemoveFromSelected = selectedItems.findIndex(i => i === insightCode);
      if (indexToRemoveFromSelected > -1) {
        selectedItems.splice(indexToRemoveFromSelected, 1);
        this.aiInsightSettingsForm.get('insightTypes').setValue(selectedItems);
      }
    }
  }

  private loadCategories(): void {
    const categorySettingValue = this.config.getSetting(this.automaticSummarizationCategories);
    const categoryIds = JSON.parse(categorySettingValue) as string[];

    if (categoryIds && categoryIds.length > 0) {
      this.crudService
        .get<CategoryDto>(CategoryDto, {
          filters: [
            {
              field: 'id',
              operator: this.operators.In,
              value: categoryIds,
            },
          ],
          sorters: [],
          skipCount: 0,
          maxResultCount: 9999,
        })
        .subscribe(response => {
          this.aiInsightSettingsForm
            .get('automaticSummarizationCategories')
            .setValue(response.items);
        });
    }
  }

  private getInsightTypes(insightData: string, aiCoachingEnabled: boolean): string[] {
    let selectedSet = new Set<string>();

    this.defaultValues.forEach(code => selectedSet.add(code));
    if (aiCoachingEnabled) {
      this.aiCoachingDefaultValues.forEach(code => selectedSet.add(code));
    }

    if (insightData) {
      insightData
        .split(';')
        .filter(x => x.length > 0)
        .forEach(code => selectedSet.add(code));
    }

    if (!aiCoachingEnabled) {
      this.aiCoachingDefaultValues.forEach(code => selectedSet.delete(code));
    }

    this.insightTypesData = Array.from(selectedSet);
    return Array.from(selectedSet);
  }

  private setTargets() {
    const aiCoachingTargetSelection = this.config.getSetting(this.aiCoachingTargetSelection);

    const targets = this.config.getSetting(this.aiCoachingTargets)
      ? this.config.getSetting(this.aiCoachingTargets).split(';')
      : [];

    if (aiCoachingTargetSelection === this.targetSelectionType.User.toString()) {
      this.userCacheService
        .getAndCacheUsers(targets)
        .pipe(take(1))
        .subscribe(users => {
          this.aiInsightSettingsForm.get('aiCoachingAgents').patchValue(users);
        });
    } else if (aiCoachingTargetSelection === this.targetSelectionType.Department.toString()) {
      this.crudService
        .get<DepartmentDto>(DepartmentDto, {
          filters: [
            {
              field: 'id',
              operator: this.operators.In,
              value: JSON.stringify(targets),
            },
          ],
          maxResultCount: 9999,
          skipCount: 0,
          sorters: [],
        })
        .subscribe(response => {
          this.aiInsightSettingsForm.get('aiCoachingDepartments').patchValue({
            selectedDepartments: response.items,
            filterModeId: DepartmentFilterModeOption.SelectedDepartments,
          });
        });
    } else if (aiCoachingTargetSelection === this.targetSelectionType.UserGroup.toString()) {
      this.crudService
        .get<UserGroupDto>(UserGroupDto, {
          filters: [
            {
              field: 'id',
              operator: this.operators.Contains,
              value: targets,
            },
          ],
          maxResultCount: 9999,
          skipCount: 0,
          sorters: [],
        })
        .pipe(take(1))
        .subscribe(response => {
          this.aiInsightSettingsForm.get('aiCoachingUserGroups').patchValue(response.items);
        });
    }
  }

  private getTargets(): string {
    const aiCoachingTargetSelection = this.aiInsightSettingsForm.get(
      'aiCoachingTargetSelection',
    ).value;

    if (aiCoachingTargetSelection === this.targetSelectionType.User.toString()) {
      return this.aiInsightSettingsForm
        .get('aiCoachingAgents')
        .value.map(x => x.id)
        .join(';');
    } else if (aiCoachingTargetSelection === this.targetSelectionType.Department.toString()) {
      return this.aiInsightSettingsForm
        .get('aiCoachingDepartments')
        .value.selectedDepartments.map(x => x.id)
        .join(';');
    } else if (aiCoachingTargetSelection === this.targetSelectionType.UserGroup.toString()) {
      return this.aiInsightSettingsForm
        .get('aiCoachingUserGroups')
        .value.map(x => x.id)
        .join(';');
    }

    return '';
  }
}
